import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useStateValue } from '@State'
import { saveAnswer } from '../../api'
import { FeedbackDisplay } from './utils/FeedbackDisplay'
import { TabSplitLayout } from './utils/TabSplitLayout'
import { SaveIndicator } from './utils/SaveIndicator'
import { FEEDBACK_BADGE, FEEDBACK_LABELS, OPTION_FEEDBACK_STYLE } from './utils/feedbackStyles'
import { stripHtml } from './utils/stripHtml'

const MATCHING_STYLES = `
.matching-react-instructions { margin-bottom: 0.75rem; text-align: left; }
.matching-react-layout { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 1.5rem; text-align: left; }
.matching-react-list { display: flex; flex-direction: column; gap: 0.5rem; margin: 0; padding: 0; list-style: none; }
.matching-react-pair-row { display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); align-items: stretch; gap: 0.5rem; }
.matching-react-item, .matching-react-option, .matching-react-slot { width: 100%; min-width: 0; border: 2px solid #6c757d; border-radius: 0.5rem; background: #fff; color: #212529; padding: 0.65rem 0.75rem; text-align: left; }
.matching-react-item { display: block; }
.matching-react-item[aria-pressed="true"] { border-color: #0069d9; background: #e7f1ff; box-shadow: 0 0 0 2px rgba(0, 105, 217, 0.25); }
.matching-react-item:focus-visible, .matching-react-option:focus-visible { outline: 3px solid #005fcc; outline-offset: 2px; }
.matching-react-connector { align-self: center; color: #6c757d; font-size: 0.8rem; font-weight: 600; }
.matching-react-slot { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; border-color: #64748b; background: #f8fafc; }
.matching-react-slot.is-empty { border-style: dashed; border-color: #adb5bd; color: #6c757d; }
.matching-react-pair-row.is-selected .matching-react-slot { border-color: #0069d9; background: #f4f8ff; color: #1f4f85; }
.matching-react-slot-content { min-width: 0; }
.matching-react-correct-answer { display: block; margin-top: 0.25rem; font-size: 0.85rem; }
.matching-react-badge { flex: 0 0 auto; font-size: 0.75rem; font-weight: 700; color: #475569; }
.matching-react-options-empty { margin: 0; padding: 0.65rem 0; color: #6c757d; }
.matching-react-option:disabled { cursor: default; opacity: 0.6; }
.matching-react-status { min-height: 1.5rem; margin-top: 0.75rem; }
@media (max-width: 64rem) { .matching-react-layout { grid-template-columns: 1fr; } }
@media (max-width: 36rem) {
  .matching-react-pair-row { grid-template-columns: 1fr; gap: 0.35rem; }
  .matching-react-connector { justify-self: start; margin-left: 0.75rem; }
}
`

export function parseMatchingAnswer(answer) {
  if (!answer) return {}
  try {
    const parsed = JSON.parse(answer)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch (e) {
    return {}
  }
}

function serializeMatchingAnswer(items, pairs) {
  const answer = {}
  items.forEach(item => { answer[item.guid] = pairs[item.guid] || '' })
  return JSON.stringify(answer)
}

function nextUnmatchedGuid(items, pairs, currentGuid) {
  const currentIndex = items.findIndex(item => item.guid === currentGuid)
  const ordered = items.slice(currentIndex + 1).concat(items.slice(0, currentIndex + 1))
  return ordered.find(item => !pairs[item.guid])?.guid || null
}

export const MatchingQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, savingGuids, readonly, showFeedback } = state
  const { showAnswer } = state.feedbackSettings
  const isSaving = savingGuids.has(question.guid)
  const pairs = useMemo(() => parseMatchingAnswer(question.answer), [question.answer])
  const [selectedItemGuid, setSelectedItemGuid] = useState(null)
  const [message, setMessage] = useState('No item selected.')
  const [messageIsError, setMessageIsError] = useState(false)
  const itemRefs = useRef({})
  const optionRefs = useRef({})
  const resetRef = useRef(null)

  const itemByGuid = useMemo(() => Object.fromEntries(
    question.data.items.map(item => [item.guid, item])
  ), [question.data.items])
  const optionByGuid = useMemo(() => Object.fromEntries(
    question.data.options.map(option => [option.guid, option])
  ), [question.data.options])
  const usedByOtherItems = useMemo(() => new Set(
    Object.entries(pairs)
      .filter(([itemGuid, optionGuid]) => optionGuid && itemGuid !== selectedItemGuid)
      .map(([, optionGuid]) => optionGuid)
  ), [pairs, selectedItemGuid])

  useEffect(() => {
    const selected = Object.values(pairs).filter(Boolean).length
    dispatch({
      type: 'SET_FULLY_ANSWERED',
      guid: question.guid,
      value: selected === question.data.items.length,
    })
  }, [question.answer, question.data.items.length])

  useEffect(() => {
    if (!selectedItemGuid) return
    const firstAvailable = question.data.options.find(option => !usedByOtherItems.has(option.guid))
    if (firstAvailable) optionRefs.current[firstAvailable.guid]?.focus()
  }, [selectedItemGuid])

  const announce = (text, isError = false) => {
    setMessage('')
    setMessageIsError(isError)
    window.setTimeout(() => setMessage(text), 0)
  }

  const focusAfterPairing = (itemGuid, nextPairs) => {
    const nextGuid = nextUnmatchedGuid(question.data.items, nextPairs, itemGuid)
    window.setTimeout(() => {
      if (nextGuid) itemRefs.current[nextGuid]?.focus()
      else resetRef.current?.focus()
    }, 0)
  }

  const savePairs = async (nextPairs, previousAnswer, onSuccess, onError) => {
    const nextAnswer = serializeMatchingAnswer(question.data.items, nextPairs)
    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: nextAnswer })
    dispatch({ type: 'SAVE_START', guid: question.guid })
    try {
      const response = await saveAnswer(
        coursenum,
        activitySubmissionId,
        question.guid,
        nextAnswer,
      )
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
      onSuccess()
    } catch (e) {
      dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: previousAnswer })
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
      onError()
    }
  }

  const selectItem = itemGuid => {
    if (readonly || isSaving) return
    setSelectedItemGuid(itemGuid)
    setMessageIsError(false)
    setMessage(
      `${pairs[itemGuid] ? 'Choose a new answer' : 'Choose an answer'} for ${stripHtml(itemByGuid[itemGuid].text)}.`
    )
  }

  const cancelSelection = () => {
    if (!selectedItemGuid) return
    const itemGuid = selectedItemGuid
    setSelectedItemGuid(null)
    announce(`Pairing canceled for ${stripHtml(itemByGuid[itemGuid].text)}.`)
    window.setTimeout(() => itemRefs.current[itemGuid]?.focus(), 0)
  }

  const chooseOption = optionGuid => {
    if (!selectedItemGuid || readonly || isSaving || usedByOtherItems.has(optionGuid)) return
    const itemGuid = selectedItemGuid
    const previousAnswer = question.answer || '{}'
    const nextPairs = { ...pairs, [itemGuid]: optionGuid }
    const itemText = stripHtml(itemByGuid[itemGuid].text)
    const optionText = stripHtml(optionByGuid[optionGuid].text)
    setSelectedItemGuid(null)
    savePairs(
      nextPairs,
      previousAnswer,
      () => {
        announce(`${itemText} paired with ${optionText}. ${Object.values(nextPairs).filter(Boolean).length} of ${question.data.items.length} items paired.`)
        focusAfterPairing(itemGuid, nextPairs)
      },
      () => {
        setSelectedItemGuid(itemGuid)
        announce(`The pairing for ${itemText} could not be saved. Try again.`, true)
      },
    )
  }

  const resetPairs = () => {
    if (readonly || isSaving) return
    const previousAnswer = question.answer || '{}'
    savePairs(
      {},
      previousAnswer,
      () => {
        setSelectedItemGuid(null)
        announce('All matching answers were reset.')
        window.setTimeout(() => itemRefs.current[question.data.items[0]?.guid]?.focus(), 0)
      },
      () => announce('The matching answers could not be reset. Try again.', true),
    )
  }

  const handleKeyDown = event => {
    if (event.key === 'Escape' && selectedItemGuid) {
      event.preventDefault()
      cancelSelection()
    }
  }

  const correctnessVisible = showFeedback && showAnswer && question.correctAnswer

  return (
    <>
      <FeedbackDisplay question={question} />
      <style>{MATCHING_STYLES}</style>
      <div className="position-relative"><SaveIndicator questionGuid={question.guid} /></div>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
        <div style={{ width: '100%' }} onKeyDown={handleKeyDown} aria-busy={isSaving}>
          <div role="heading" aria-level={3} className="mb-2 text-left" dangerouslySetInnerHTML={{ __html: question.text }} />
          {!readonly && (
            <p id={`matching-instructions-${question.guid}`} className="matching-react-instructions">
              Choose an item, then choose the answer to pair with it. Press Escape to cancel a selection.
            </p>
          )}
          <div className="matching-react-layout">
            <section aria-labelledby={`matching-items-heading-${question.guid}`}>
              <h4 id={`matching-items-heading-${question.guid}`}>Your matches</h4>
              <ul className="matching-react-list">
                {question.data.items.map(item => {
                  const optionGuid = pairs[item.guid]
                  const option = optionByGuid[optionGuid]
                  const selected = selectedItemGuid === item.guid
                  const correctOption = correctnessVisible
                    ? optionByGuid[question.correctAnswer[item.guid]]
                    : null
                  const correct = correctnessVisible && optionGuid === question.correctAnswer[item.guid]
                  const feedbackStatus = correctnessVisible
                    ? (correct ? 'correct' : optionGuid ? 'incorrect' : 'missed')
                    : null
                  const displayedOption = selected ? null : option
                  const displayedFeedbackStatus = selected ? null : feedbackStatus
                  const slotId = `matching-slot-${question.guid}-${item.guid}`
                  return (
                    <li
                      key={item.guid}
                      className={`matching-react-pair-row${selected ? ' is-selected' : ''}`}
                    >
                      {readonly ? (
                        <div className="matching-react-item">
                          <span dangerouslySetInnerHTML={{ __html: item.text }} />
                        </div>
                      ) : (
                        <button
                          type="button"
                          ref={element => { itemRefs.current[item.guid] = element }}
                          className="matching-react-item"
                          aria-pressed={selected}
                          aria-describedby={`matching-instructions-${question.guid} ${slotId}`}
                          disabled={isSaving}
                          onClick={() => selectItem(item.guid)}
                        >
                          <span dangerouslySetInnerHTML={{ __html: item.text }} />
                        </button>
                      )}
                      <span className="matching-react-connector" aria-hidden="true">with</span>
                      <div
                        id={slotId}
                        className={`matching-react-slot${displayedOption ? '' : ' is-empty'}`}
                        style={displayedFeedbackStatus
                          ? OPTION_FEEDBACK_STYLE[displayedFeedbackStatus]
                          : undefined}
                      >
                        {displayedOption ? (
                          <>
                            <span className="matching-react-slot-content">
                              <span className="sr-only">Paired answer: </span>
                              <span dangerouslySetInnerHTML={{ __html: displayedOption.text }} />
                              {displayedFeedbackStatus && displayedFeedbackStatus !== 'correct' && correctOption && (
                                <span className="matching-react-correct-answer">
                                  <span>Correct answer: </span>
                                  <span dangerouslySetInnerHTML={{ __html: correctOption.text }} />
                                </span>
                              )}
                            </span>
                            {!displayedFeedbackStatus && <span className="matching-react-badge">Paired</span>}
                            {displayedFeedbackStatus && (
                              <span style={FEEDBACK_BADGE[displayedFeedbackStatus]}>
                                {FEEDBACK_LABELS[displayedFeedbackStatus]}
                              </span>
                            )}
                          </>
                        ) : (
                          <span>
                            {selected ? 'Choose from available answers' : 'No answer selected'}
                            {displayedFeedbackStatus === 'missed' && correctOption && (
                              <span className="matching-react-correct-answer">
                                <span>Correct answer: </span>
                                <span dangerouslySetInnerHTML={{ __html: correctOption.text }} />
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </section>

            {!readonly && (
              <section aria-labelledby={`matching-options-heading-${question.guid}`}>
                <h4 id={`matching-options-heading-${question.guid}`}>Available answers</h4>
                <div className="matching-react-list" role="group" aria-label="Available answers">
                  {question.data.options.map(option => {
                    const usedElsewhere = usedByOtherItems.has(option.guid)
                    return (
                      <button
                        type="button"
                        key={option.guid}
                        ref={element => { optionRefs.current[option.guid] = element }}
                        className="matching-react-option"
                        hidden={usedElsewhere}
                        disabled={!selectedItemGuid || isSaving || usedElsewhere}
                        aria-describedby={`matching-status-${question.guid}`}
                        onClick={() => chooseOption(option.guid)}
                        dangerouslySetInnerHTML={{ __html: option.text }}
                      />
                    )
                  })}
                  {!question.data.options.some(option => !usedByOtherItems.has(option.guid)) && (
                    <p className="matching-react-options-empty">
                      All answers are paired. Select an item to change its answer.
                    </p>
                  )}
                </div>
              </section>
            )}
          </div>

          {!readonly && (
            <button
              type="button"
              ref={resetRef}
              className="btn btn-sm btn-outline-secondary mt-3"
              disabled={isSaving || !Object.values(pairs).some(Boolean)}
              onClick={resetPairs}
            >
              Reset Answers
            </button>
          )}
          {!readonly && (
            <div
              id={`matching-status-${question.guid}`}
              className={`matching-react-status${messageIsError ? ' text-danger' : ' text-muted'}`}
              role={messageIsError ? 'alert' : 'status'}
              aria-live={messageIsError ? 'assertive' : 'polite'}
              aria-atomic="true"
            >
              {message}
            </div>
          )}
        </div>
      </TabSplitLayout>
    </>
  )
}
